import { useEffect, useRef, useState } from "react";
import {
  Button,
  Input,
  InputNumber,
} from "../../components/ReadonlyAwareInputs";

const MAX_INT = 1e16;

const canAddRow = (tableData) => {
  if (tableData && tableData?.length > 0) {
    return (
      tableData[0]?.value &&
      tableData?.slice(1)?.every((row) => {
        if (!row?.description || !row?.value) {
          return false;
        }

        return true;
      })
    );
  }
  return true;
};

export default function DynamicTable({
  inputTableData = [[]],
  updateParentTableData,
  setIsError,
  valueType = "",
  updateCount,
  setUpdateCount,
}) {
  const valueMin = valueType === "positive" ? 0 : -MAX_INT;
  const valueMax = valueType === "negative" ? 0 : MAX_INT;
  const [validators, setValidators] = useState(
    inputTableData?.map((data) => {
      return true;
    }),
  );

  const [tableData, updateTableData] = useState([]);

  const inputRefs = useRef([]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      updateTableData(
        inputTableData?.map((data, index) => ({ ...data, key: index })),
      );
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [inputTableData]);

  const generateKey = () => {
    const keys = tableData.map((row) => row.key);
    let key = 0;
    while (keys.includes(key)) {
      key++;
    }
    return key;
  };

  const addTableRow = () => {
    if (canAddRow(tableData)) {
      updateTableData((prevState) => [
        ...prevState,
        { description: "", value: "", key: generateKey() },
      ]);
      updateParentTableData((prevState) => [
        ...prevState,
        { description: "", value: "" },
      ]);
      setUpdateCount((prev) => prev + 1);
      const timeoutId = setTimeout(() => {
        if (inputRefs?.current?.length > 0) {
          inputRefs?.current[inputRefs?.current?.length - 1]?.focus();
        }
      }, 50);
      return () => clearTimeout(timeoutId);
    }
  };

  const deleteTableRows = (index) => {
    if (tableData?.length === 1) {
      updateTableData([{ description: "", value: "", key: generateKey() }]);
      updateParentTableData([{ description: "", value: "" }]);
      setUpdateCount((prev) => prev + 1);
    } else {
      inputRefs.current = inputRefs?.current?.filter((_, i) => i !== index);

      updateTableData((prevState) => {
        const rows = [...prevState];
        return rows?.filter((_, i) => i !== index);
      });
      updateParentTableData((prevState) => {
        const rows = [...prevState];
        return rows?.filter((_, i) => i !== index);
      });
      setUpdateCount((prev) => prev + 1);
    }
  };

  const isDescriptionValid = (index, desc, val) => {
    return !(index > 0 && val ? !desc || desc?.length === 0 : false);
  };

  const isValueValid = (val) => {
    return !(val > valueMax || val < valueMin);
  };

  const tableChange = (index, key, value) => {
    const rowsInput = [...tableData];
    rowsInput[index][key] = value;

    const isValid =
      isDescriptionValid(
        index,
        rowsInput[index]["description"],
        rowsInput[index]["value"],
      ) && isValueValid(rowsInput[index]["value"]);

    updateTableData(rowsInput);
    updateParentTableData(rowsInput);
  };

  useEffect(() => {
    const newValidators = [];
    tableData.forEach((data, index) => {
      const isValid =
        isDescriptionValid(index, data["description"], data["value"]) &&
        isValueValid(data["value"]);

      newValidators.push(isValid);
    });
    setValidators(newValidators);
  }, [tableData]);

  useEffect(() => {
    setIsError(validators?.includes(false));
  }, [validators]);

  const handleKeyDown = (event, index) => {
    if (event?.key === "Enter" && index === tableData?.length - 1) {
      addTableRow();
    }
  };

  return (
    <div>
      <table className="table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th className="col" key={1}>
              Description
            </th>
            <th className="col" key={2}>
              Value
            </th>
            <th style={{ width: "2%" }}>
              <Button
                add
                disabled={!canAddRow(tableData)}
                className="btn btn-outline-success"
                onClick={() => addTableRow()}
              >
                <span className="Appkit4-icon icon-plus-outline"></span>
              </Button>
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData?.map((data, index) => {
            return (
              <tr key={`${data["key"]}${updateCount}`}>
                <td key={1}>
                  <Input
                    inputRef={(el) => (inputRefs.current[index] = el)}
                    value={data["description"]}
                    onChange={(val) => tableChange(index, "description", val)}
                    error={
                      !isDescriptionValid(
                        index,
                        data["description"],
                        data["value"],
                      )
                    }
                  />
                </td>
                <td key={2}>
                  <InputNumber
                    format=","
                    value={data["value"]}
                    onChange={(val) => tableChange(index, "value", val)}
                    error={!isValueValid(data["value"])}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                  />
                </td>
                <td>
                  <Button
                    className="btn btn-outline-danger"
                    onClick={() => deleteTableRows(index)}
                  >
                    <span className="Appkit4-icon icon-close-outline"></span>
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
